import React, {useEffect, useLayoutEffect, useMemo, useState} from "react";
import {Box, Drawer, IconButton, CircularProgress, Stack} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CervinoText from "../../typography/CervinoText";
import LeagueSpartanText from "../../typography/LeagueSpartanText";
import SMButtonWhite from "../../navigation/SMButtonWhite";
import {CervinoHeaderText} from "../../typography/CervinoHeaderText";
import DrawerImageGallery from "../../layout/DrawerImageGallery";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { trackEvent } from "../../../utils/analytics";

function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
}

function darkenHex(hex, amount = 0.45) {
    if (!hex || typeof hex !== "string") return "#000000";
    const h = hex.trim().replace("#", "");

    // only supports #RRGGBB (extend later if needed)
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return "#000000";

    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);

    const k = clamp(1 - amount, 0, 1);

    const nr = Math.round(r * k).toString(16).padStart(2, "0");
    const ng = Math.round(g * k).toString(16).padStart(2, "0");
    const nb = Math.round(b * k).toString(16).padStart(2, "0");

    return `#${nr}${ng}${nb}`;
}

function ClaimsRow({ claims = [] }) {
    if (!claims?.length) return null;

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                // spread evenly across the full width
                justifyContent: "space-between",
                width: "100%",
                // ✅ hard cap so images can never blow the layout up
                height: { xs: 100, sm: 120, md: 150, lg: 150, xl: 150 },
            }}
        >
            {claims.map((claim, idx) => (
                <Box
                    key={`${claim?.alt || "claim"}-${idx}`}
                    title={claim?.alt || ""}
                    sx={{
                        // ✅ each claim gets an equal “slot” so spacing is consistent
                        flex: "1 1 0",
                        minWidth: 0,

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        // ✅ ensure the img is constrained by this slot
                        height: "100%",
                    }}
                >
                    <Box
                        component="img"
                        src={claim?.src}
                        alt={claim?.alt || ""}
                        sx={{
                            // ✅ never exceed the slot
                            maxWidth: "100%",
                            maxHeight: "100%",
                            width: "auto",
                            height: "auto",
                            objectFit: "contain",
                            display: "block",
                        }}
                    />
                </Box>
            ))}
        </Box>
    );
}

function MediaPanel({
                        name,
                        images = [],
                        imageAlt,
                        claims,
                        imageLoaded,
                        imageError,
                        onLoad,
                        onError,
                    }) {
    return (
        <Box>
            <ClaimsRow claims={claims} />

            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    px: { xs: 2, sm: 3 },
                }}
            >
                {/* Spinner overlay while hero loads */}
                {!imageLoaded && !imageError && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                            zIndex: 2,
                        }}
                    >
                        <CircularProgress size={64} color="inherit" />
                    </Box>
                )}

                {!imageError ? (
                    <Box sx={{ width: "100%", paddingTop: 1 }}>
                        <DrawerImageGallery
                            images={images}
                            alt={imageAlt || name || "Product image"}
                            heroHeight={{ xs: 280, sm: 400, md: 600, lg: 500, xl: 550 }}
                            radius={30}
                            maxThumbs={2}
                            onHeroLoad={onLoad}
                            onHeroError={onError}
                            lightboxBackdrop="rgba(40, 35, 25, 0.55)"
                            lightboxBlur={6}
                        />
                    </Box>
                ) : (
                    <Box sx={{ pb: 6 }}>
                        <CervinoText>Image unavailable</CervinoText>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function ContentPanel({
                          name,
                          description,
                          price,
                          productLinkUrl,
                          productSellingDescription,
                          onPrev,
                          onNext,
                          hasPrev,
                          hasNext,
                      }) {
    const normalizeUrl = (url) => (typeof url === "string" ? url.trim() : "");
    const hasValidUrl = Boolean(normalizeUrl(productLinkUrl));
    const isSoldOut = !hasValidUrl;

    const handleBuy = () => {
        if (!hasValidUrl) return;

        const raw = normalizeUrl(productLinkUrl);
        const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

        trackEvent("select_content", {
            content_type: "buy_now",
            item_name: name,
            destination_url: href,
        });

        window.open(href, "_blank", "noopener,noreferrer");
    };

    return (
        <Stack spacing={2} sx={{ px: { xs: 2, sm: 3 } }}>
            {/* Title */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <CervinoHeaderText
                    sx={{
                        fontSize: { xs: "1.5rem", sm: "2rem", md: "3rem", lg: "2rem", xl: "2rem" },
                        textAlign: "left",
                        textTransform: "uppercase",
                    }}
                >
                    {name}
                </CervinoHeaderText>
            </Stack>

            {/* Description */}
            <LeagueSpartanText
                sx={{
                    fontWeight: 400,
                    fontSize: { xs: "1rem", sm: "1.5rem", md: "1.8rem", lg: "1.5rem", xl: "1.5rem" },
                    textTransform: "uppercase",
                }}
            >
                {description}
            </LeagueSpartanText>

            {/* ✅ XS: price + sellingDescription + arrows in ONE ROW */}
            <Box
                sx={{
                    display: { xs: "flex", sm: "flex", md: "flex", lg: "none", xl: "none" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    width: "100%",
                }}
            >
                {/* left side: price + description */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
                    <Box sx={{ borderRadius: 3, border: "1px solid", py: 1, px: 2, flexShrink: 0 }}>
                        <CervinoText sx={{ fontWeight: 900, fontSize: 18 }}>{price}</CervinoText>
                    </Box>

                    {productSellingDescription ? (
                        <LeagueSpartanText
                            sx={{
                                fontSize: 16,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {productSellingDescription}
                        </LeagueSpartanText>
                    ) : null}
                </Box>

                {/* right side: arrows */}
                <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                    <IconButton
                        onClick={onPrev}
                        disabled={!hasPrev}
                        sx={{ color: "white" }}
                        aria-label="Previous product"
                    >
                        <ChevronLeftIcon sx={{ fontSize: 40 }} />
                    </IconButton>

                    <IconButton
                        onClick={onNext}
                        disabled={!hasNext}
                        sx={{ color: "white" }}
                        aria-label="Next product"
                    >
                        <ChevronRightIcon sx={{ fontSize: 40 }} />
                    </IconButton>
                </Box>
            </Box>

            <Box>
                <SMButtonWhite fullWidth onClick={handleBuy} disabled={isSoldOut}>
                    {isSoldOut ? "SOLD OUT" : "BUY NOW"}
                </SMButtonWhite>
            </Box>
        </Stack>
    );
}

export default function ProductDrawer({
                                          open,
                                          onClose,
                                          product,
                                          onPrev,
                                          onNext,
                                          hasPrev,
                                          hasNext,
                                      }) {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const backgroundColor = product?.backgroundColor || "#E9ECEF";
    const paperBgImage = useMemo(
        () =>
            `linear-gradient(180deg, rgba(0,0,0,.75) 0%, rgba(0,0,0,.15) 45%, rgba(0,0,0,0) 55%)`,
        []
    );
    const chromeColor = useMemo(
        () => darkenHex(backgroundColor, 0.75),
        [backgroundColor]
    );

    useEffect(() => {
        setImageLoaded(false);
        setImageError(false);
    }, [product?.id, open]);

    useLayoutEffect(() => {
        if (!open || !product) return;

        let meta = document.querySelector('meta[name="theme-color"]');
        const created = !meta;

        if (!meta) {
            meta = document.createElement("meta");
            meta.setAttribute("name", "theme-color");
            document.head.appendChild(meta);
        }

        const prev = meta.getAttribute("content");
        meta.setAttribute("content", chromeColor || "#000000");

        return () => {
            if (created) meta.parentNode?.removeChild(meta);
            else if (prev) meta.setAttribute("content", prev);
        };
    }, [open, chromeColor, product]);

    if (!product) return null;

    const { name, description, imageAlt, images, claims = [], price, productLinkUrl, productSellingDescription } =
        product;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        width: { xs: "100vw", sm: "100vw", md: 760, lg: 920, xl: 1040 },
                        maxWidth: "100vw",
                        color: "white",

                        // ✅ important: separate color + image (Safari repaints better)
                        backgroundColor,                // solid base
                        backgroundImage: paperBgImage,  // gradient overlay
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "cover",

                        height: "100dvh",
                        maxHeight: "100dvh",
                        overflow: "hidden",
                        overscrollBehavior: "none",
                        touchAction: "pan-x",
                        WebkitOverflowScrolling: "auto",
                    },
                },
            }}
        >
            {/* ✅ Root layout: header row + content row (prevents overlap) */}
            <Box
                sx={{
                    height: "100%",
                    display: "grid",
                    gridTemplateRows: "40px 1fr", // row for close icon + rest content
                }}
            >
                {/* Header row */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        px: 2,
                        position: "sticky",
                        top: 0,
                        zIndex: 20,
                    }}
                >
                    <IconButton onClick={onClose} sx={{ color: "white" }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Content row */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "60% 40%" },
                        gridTemplateRows: "0fr 0fr"
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <MediaPanel
                            name={name}
                            images={images}
                            imageAlt={imageAlt}
                            claims={claims}
                            imageLoaded={imageLoaded}
                            imageError={imageError}
                            onLoad={() => setImageLoaded(true)}
                            onError={() => setImageError(true)}
                        />
                    </Box>

                    <Box
                        sx={{
                            minWidth: 0,
                            display: "grid",
                            placeItems: { xs: "start", lg: "center" },
                            position: "relative",
                        }}
                    >
                        {/* ✅ DESKTOP NAV (fixed spot, fluent transitions) */}
                        <Box
                            sx={{
                                display: { xs: "none", lg: "flex" },
                                position: "absolute",
                                top: 24,
                                right: 24,
                                zIndex: 30,
                            }}
                        >
                            <IconButton
                                onClick={onPrev}
                                disabled={!hasPrev}
                                sx={{ color: "white" }}
                                aria-label="Previous product"
                            >
                                <ChevronLeftIcon sx={{ fontSize: { xs: 40, sm: 50 } }} />
                            </IconButton>

                            <IconButton
                                onClick={onNext}
                                disabled={!hasNext}
                                sx={{ color: "white" }}
                                aria-label="Next product"
                            >
                                <ChevronRightIcon sx={{ fontSize: { xs: 40, sm: 50 } }} />
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Box
                            sx={{
                                width: "100%",
                                marginBottom: {xs: 10, sm: 10, md: 20, lg: 20, xl: 20},
                            }}
                        >
                            <ContentPanel
                                name={name}
                                description={description}
                                price={price}
                                productLinkUrl={productLinkUrl}
                                productSellingDescription={productSellingDescription}
                                onPrev={onPrev}
                                onNext={onNext}
                                hasPrev={hasPrev}
                                hasNext={hasNext}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
}
